import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Select,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import { FaEllipsisV, FaPlus } from "react-icons/fa";

import { SupplementHistoryEntry, addSupplementHistory, ceaseSupplementHistory } from "~/api/members";
import { Member, useAppStore } from "~/store";
import { useDebugStore } from "~/store/debugStore";
import { GroupMember } from "~/types/group";

const formatDate = (dateStr: string): string => {
  if (!dateStr) return "";
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString();
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateStr;
  }
};

// Format date as YYYY-MM-DD for input fields
const formatDateForInput = (date: Date): string => {
  return date.toISOString().split("T")[0];
};

// Get today's date formatted for input
const getTodayFormatted = (): string => {
  return formatDateForInput(new Date());
};

// Helper function to sort supplements by name and then by dosage amount
const sortSupplements = (supplements: SupplementHistoryEntry[]): SupplementHistoryEntry[] => {
  return [...supplements].sort((a, b) => {
    // First sort by supplement name (case-insensitive)
    const nameA = a.dosage.supplement.toLowerCase();
    const nameB = b.dosage.supplement.toLowerCase();

    const nameComparison = nameA.localeCompare(nameB);
    if (nameComparison !== 0) {
      return nameComparison;
    }

    // If supplement names are the same, sort by dosage amount (ascending)
    const amountA = a.dosage.amount || 0;
    const amountB = b.dosage.amount || 0;
    return amountA - amountB;
  });
};

// Available supplement options
const supplementOptions = [
  "Acidic Calcium",
  "Alkaline Calcium",
  "Colloidal Caps",
  "Baking Soda",
  "Vitamin B12",
  "Ionic Iron",
  "Vitamin D3",
  "Magnesium Glycinate",
  "Zinc",
  "Other"
];

// Available dosage unit options
const unitOptions = [
  { value: "tsp", label: "Teaspoons (tsp)" },
  { value: "mg", label: "Milligrams (mg)" },
  { value: "pills", label: "Pills" },
  { value: "mcg", label: "Micrograms (mcg)" },
  { value: "drops", label: "Drops" },
  { value: "other", label: "Other" }
];

// Available frequency options
const frequencyOptions = [
  { value: "daily", label: "Daily" },
  { value: "twice_daily", label: "Twice Daily" },
  { value: "every_other_day", label: "Every Other Day" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "as_needed", label: "As Needed" }
];

interface CurrentSupplementsProps {
  supplements: SupplementHistoryEntry[] | null | undefined;
}

const CurrentSupplementsComponent = ({ supplements = [] }: CurrentSupplementsProps) => {
  const currentMember = useAppStore((state) => state.currentMember) as Member | undefined;
  const currentGroupMember = useAppStore((state) => state.currentGroupMember) as GroupMember | undefined;
  const debugMode = useAppStore((state) => state.debugMode);
  const tallyRender = useDebugStore((state) => state.tallyRender);
  const [activeSupplements, setActiveSupplements] = useState<SupplementHistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state for adding new supplement
  const [newSupplement, setNewSupplement] = useState({
    supplement: "",
    amount: "",
    unit: "",
    frequency: "daily", // Default to daily
    startDate: getTodayFormatted(),
    notes: ""
  });
  const [otherSupplementName, setOtherSupplementName] = useState("");
  const [otherUnitName, setOtherUnitName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Modal controls
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  // Memoize color values to prevent recalculation
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("blue.100", "blue.700");
  const textColor = useColorModeValue("gray.700", "gray.200");
  const headerBg = useColorModeValue("blue.50", "blue.900");
  const emptyTextColor = useColorModeValue("gray.500", "gray.400");
  const addButtonColor = useColorModeValue("blue.500", "blue.300");
  const lightbulbColor = useColorModeValue("green.500", "green.300");

  // Track render count
  useEffect(() => {
    tallyRender("CurrentSupplements");
  }, [tallyRender]);

  // Update active supplements when the supplements prop changes
  useEffect(() => {
    // Sort supplements before setting state
    const safeSupplements = Array.isArray(supplements) ? supplements : [];
    const sortedSupplements = sortSupplements(safeSupplements || []);
    setActiveSupplements(sortedSupplements);
  }, [supplements]);

  // Function to force refresh data (e.g., after adding/removing supplements)
  const refreshData = useCallback(() => {
    // Just emit an event that the MembersPage can listen to
    const refreshEvent = new CustomEvent("member-supplements-changed", {
      detail: { groupMemberId: currentGroupMember?.id }
    });
    window.dispatchEvent(refreshEvent);
  }, [currentGroupMember?.id]);

  // Handle No Longer Taking
  const handleNoLongerTaking = useCallback(
    async (supplement: SupplementHistoryEntry) => {
      if (!currentGroupMember) {
        console.error("No current group member available");
        toast({
          title: "Error",
          description: "Unable to update supplement - no current group member",
          status: "error",
          duration: 5000,
          isClosable: true
        });
        return;
      }

      try {
        setIsLoading(true);
        const response = await ceaseSupplementHistory(currentGroupMember.id, supplement.dosage.supplement);

        if (response.success) {
          toast({
            title: "Success",
            description: `Marked ${supplement.dosage.supplement} as no longer taking`,
            status: "success",
            duration: 3000,
            isClosable: true
          });

          // Remove the supplement from the list and notify parent about the change
          setActiveSupplements((prev) => prev.filter((item) => item.id !== supplement.id));
          refreshData();
        } else {
          toast({
            title: "Error",
            description: response.error || "Failed to update supplement",
            status: "error",
            duration: 5000,
            isClosable: true
          });
        }
      } catch (error) {
        console.error("Error updating supplement:", error);
        toast({
          title: "Error",
          description: "An unexpected error occurred",
          status: "error",
          duration: 5000,
          isClosable: true
        });
      } finally {
        setIsLoading(false);
      }
    },
    [currentGroupMember, toast, refreshData]
  );

  // Handle the "Modify dose" option
  const handleModifyDose = (supplement: SupplementHistoryEntry) => {
    console.log("Modify dose:", supplement.dosage.supplement);
    // Functionality will be added later
  };

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewSupplement((prev) => ({ ...prev, [name]: value }));
  };

  // Handle number input changes
  const handleNumberChange = (valueAsString: string) => {
    setNewSupplement((prev) => ({ ...prev, amount: valueAsString }));
  };

  // Reset form
  const resetForm = () => {
    setNewSupplement({
      supplement: "",
      amount: "",
      unit: "",
      frequency: "daily",
      startDate: getTodayFormatted(),
      notes: ""
    });
    setOtherSupplementName("");
    setOtherUnitName("");
  };

  // Close modal and reset form
  const handleCloseModal = () => {
    resetForm();
    onClose();
  };

  // Helper to format dosage with frequency
  const formatDosage = (amount?: number | null, unit?: string | null, frequency?: string | null) => {
    if (!amount && !unit && !frequency) return "—";

    let dosage = "";
    if (amount) dosage += amount;
    if (unit) dosage += dosage ? ` ${unit}` : unit;

    // Add frequency if available
    if (frequency) {
      // Convert frequency value to a more readable format with no spaces around slash
      const frequencyMap: Record<string, string> = {
        daily: "/day",
        twice_daily: "/day (twice)",
        every_other_day: "/every other day",
        weekly: "/week",
        monthly: "/month",
        as_needed: "/as needed"
      };

      // Ensure we remove any spaces before the slash
      const formattedFrequency = frequencyMap[frequency] || `/${frequency}`;

      // Make sure there's no space after the unit before adding the slash
      dosage += formattedFrequency;
    }

    return dosage;
  };

  // Helper to format context type
  const formatContextType = (contextType: string) => {
    if (!contextType) return "—";

    // Convert snake_case to readable format
    return contextType
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Memoize the submit handler
  const handleSubmit = useCallback(async () => {
    if (!currentGroupMember) {
      console.error("No current group member available");
      toast({
        title: "Error",
        description: "Unable to add supplement - no current group member",
        status: "error",
        duration: 5000,
        isClosable: true
      });
      return;
    }

    // Validate form
    if (!newSupplement.supplement) {
      toast({
        title: "Error",
        description: "Please select a supplement",
        status: "error",
        duration: 3000,
        isClosable: true
      });
      return;
    }

    if (newSupplement.supplement === "Other" && !otherSupplementName) {
      toast({
        title: "Error",
        description: "Please enter the supplement name",
        status: "error",
        duration: 3000,
        isClosable: true
      });
      return;
    }

    if (!newSupplement.amount) {
      toast({
        title: "Error",
        description: "Please enter the dosage amount",
        status: "error",
        duration: 3000,
        isClosable: true
      });
      return;
    }

    if (!newSupplement.unit) {
      toast({
        title: "Error",
        description: "Please select a unit",
        status: "error",
        duration: 3000,
        isClosable: true
      });
      return;
    }

    if (newSupplement.unit === "other" && !otherUnitName) {
      toast({
        title: "Error",
        description: "Please enter the unit name",
        status: "error",
        duration: 3000,
        isClosable: true
      });
      return;
    }

    try {
      setIsSubmitting(true);

      // Prepare supplement data in the correct format
      const supplement = newSupplement.supplement === "Other" ? otherSupplementName : newSupplement.supplement;
      const unit = newSupplement.unit === "other" ? otherUnitName : newSupplement.unit;

      // Correct format for addSupplementHistory - it needs groupMemberId and supplementData object
      const supplementData = {
        supplement: supplement,
        amount: newSupplement.amount ? parseFloat(newSupplement.amount) : undefined,
        dosageUnit: unit,
        frequency: newSupplement.frequency,
        note: newSupplement.notes || undefined
      };

      const response = await addSupplementHistory(currentGroupMember.id, supplementData);

      if (response.success) {
        toast({
          title: "Success",
          description: `Added ${supplement} to current supplements`,
          status: "success",
          duration: 3000,
          isClosable: true
        });

        // Close modal and reset form
        resetForm();
        onClose();

        // Notify parent about the change
        refreshData();
      } else {
        toast({
          title: "Error",
          description: response.error || "Failed to add supplement",
          status: "error",
          duration: 5000,
          isClosable: true
        });
      }
    } catch (error) {
      console.error("Error adding supplement:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        status: "error",
        duration: 5000,
        isClosable: true
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [currentGroupMember, newSupplement, otherSupplementName, otherUnitName, toast, resetForm, onClose, refreshData]);

  // Memoize quick add handler
  const handleQuickAddSupplement = useCallback(
    async (supplement: SupplementHistoryEntry) => {
      if (!currentGroupMember) {
        console.error("No current group member available");
        toast({
          title: "Error",
          description: "Unable to add supplement - no current group member",
          status: "error",
          duration: 5000,
          isClosable: true
        });
        return;
      }

      try {
        setIsLoading(true);

        // Prepare supplement data in the correct format
        const supplementData = {
          supplement: supplement.dosage.supplement,
          amount: supplement.dosage.amount ? Number(supplement.dosage.amount) : undefined,
          dosageUnit: supplement.dosage.unit || undefined,
          frequency: supplement.dosage.frequency || undefined,
          note: supplement.dosage.note || undefined
        };

        const response = await addSupplementHistory(currentGroupMember.id, supplementData);

        if (response.success) {
          toast({
            title: "Success",
            description: `Added ${supplement.dosage.supplement} to current supplements`,
            status: "success",
            duration: 3000,
            isClosable: true
          });

          // Refresh data
          refreshData();
        } else {
          toast({
            title: "Error",
            description: response.error || "Failed to add supplement",
            status: "error",
            duration: 5000,
            isClosable: true
          });
        }
      } catch (error) {
        console.error("Error adding supplement:", error);
        toast({
          title: "Error",
          description: "An unexpected error occurred",
          status: "error",
          duration: 5000,
          isClosable: true
        });
      } finally {
        setIsLoading(false);
      }
    },
    [currentGroupMember, toast, refreshData]
  );

  // Memoize the supplements table rendering
  const renderSupplementsTable = useMemo(() => {
    return (supplements: SupplementHistoryEntry[]) => (
      <Table variant="simple" size="sm" className="current-supplements-table">
        <Thead>
          <Tr>
            <Th>Supplement</Th>
            <Th>Dosage</Th>
            <Th>Started</Th>
            <Th width="40px"></Th>
          </Tr>
        </Thead>
        <Tbody>
          {supplements.map((supplement) => (
            <Tr key={supplement.id}>
              <Td fontWeight="medium">{supplement.dosage.supplement}</Td>
              <Td>{formatDosage(supplement.dosage.amount, supplement.dosage.unit, supplement.dosage.frequency)}</Td>
              <Td>{formatDate(supplement.startDate)}</Td>
              <Td>
                <Menu>
                  <MenuButton as={IconButton} aria-label="Options" icon={<FaEllipsisV />} variant="ghost" size="sm" />
                  <MenuList>
                    <MenuItem onClick={() => handleNoLongerTaking(supplement)}>No longer taking</MenuItem>
                    <MenuItem isDisabled onClick={() => handleModifyDose(supplement)}>
                      Modify dose
                    </MenuItem>
                  </MenuList>
                </Menu>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    );
  }, [handleNoLongerTaking, handleModifyDose, formatDosage]);

  // Don't render if no current member exists
  if (!currentMember) {
    console.log("No current member, returning null");
    return null;
  }

  return (
    <>
      <Card
        bg={cardBg}
        borderColor={borderColor}
        borderWidth="2px"
        borderRadius="lg"
        boxShadow="lg"
        height="100%"
        minH="300px"
        position="relative"
        overflow="visible"
        mt={6}
      >
        <CardHeader bg={headerBg} py={4} px={5} borderBottom="1px" borderColor={borderColor}>
          <Flex justify="space-between" align="center">
            <Flex align="center">
              <Heading size="md" color={textColor}>
                Current Supplements
              </Heading>
            </Flex>
            <Button
              onClick={onOpen}
              colorScheme="blue"
              variant="solid"
              size="sm"
              leftIcon={<FaPlus />}
              isDisabled={!currentGroupMember}
            >
              Add
            </Button>
          </Flex>
        </CardHeader>

        <CardBody>
          {isLoading ? (
            <Box>
              <Skeleton height="30px" width="100%" mb={4} />
              <Skeleton height="30px" width="100%" mb={4} />
              <Skeleton height="30px" width="100%" mb={4} />
            </Box>
          ) : error ? (
            <Text color="red.500" fontSize="sm">
              {error}
            </Text>
          ) : activeSupplements.length > 0 ? (
            renderSupplementsTable(activeSupplements)
          ) : (
            <Flex direction="column" align="center" justify="center" py={10} textAlign="center">
              <Text fontSize="md" color={emptyTextColor} mb={3}>
                No supplements currently being taken.
              </Text>
              <Text fontSize="sm" color={emptyTextColor} mb={5}>
                Use the "Add Supplement" button to track supplements this member is currently taking.
              </Text>
            </Flex>
          )}
        </CardBody>
      </Card>

      {/* Add Supplement Modal */}
      <Modal isOpen={isOpen} onClose={handleCloseModal} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add New Supplement</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <FormControl mb={4} isRequired>
              <FormLabel>Supplement</FormLabel>
              <Select
                name="supplement"
                value={newSupplement.supplement}
                onChange={handleInputChange}
                placeholder="Select supplement"
              >
                {supplementOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Select>
            </FormControl>

            {newSupplement.supplement === "Other" && (
              <FormControl mb={4} isRequired>
                <FormLabel>Supplement Name</FormLabel>
                <Input
                  value={otherSupplementName}
                  onChange={(e) => setOtherSupplementName(e.target.value)}
                  placeholder="Enter supplement name"
                />
              </FormControl>
            )}

            <FormControl mb={4}>
              <FormLabel>Dosage</FormLabel>
              <Flex gap={4}>
                <Box flex="1">
                  <FormLabel fontSize="sm" mb={1}>
                    Amount
                  </FormLabel>
                  <NumberInput min={0} value={newSupplement.amount} onChange={handleNumberChange}>
                    <NumberInputField placeholder="e.g. 500" />
                  </NumberInput>
                </Box>

                <Box flex="1">
                  <FormLabel fontSize="sm" mb={1}>
                    Unit
                  </FormLabel>
                  <Select name="unit" value={newSupplement.unit} onChange={handleInputChange} placeholder="Select unit">
                    {unitOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                  {newSupplement.unit === "other" && (
                    <Input
                      mt={2}
                      value={otherUnitName}
                      onChange={(e) => setOtherUnitName(e.target.value)}
                      placeholder="Enter custom unit"
                      size="sm"
                    />
                  )}
                </Box>

                <Box flex="1">
                  <FormLabel fontSize="sm" mb={1}>
                    Frequency
                  </FormLabel>
                  <Select name="frequency" value={newSupplement.frequency} onChange={handleInputChange}>
                    {frequencyOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </Box>
              </Flex>
            </FormControl>

            <FormControl mb={4} isRequired>
              <FormLabel>Start Date</FormLabel>
              <Input
                type="date"
                name="startDate"
                value={newSupplement.startDate}
                onChange={handleInputChange}
                max={getTodayFormatted()}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Notes</FormLabel>
              <Textarea
                name="notes"
                value={newSupplement.notes}
                onChange={handleInputChange}
                placeholder="Why is this supplement being taken? (optional)"
                resize="vertical"
                rows={3}
              />
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={handleSubmit} isLoading={isSubmitting}>
              Save
            </Button>
            <Button onClick={handleCloseModal}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

// Export the component wrapped in React.memo for optimal performance
export default React.memo(CurrentSupplementsComponent);
